import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrencyWithMillionLabel } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Card, Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteCoverageLimit, useFilterCoverageLimits } from "../../../../common/api/queries/coverageLimit";
import ActionTextIcon from "../../../../common/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "../../../../common/components/icons/PopconfirmDeleteIcon";
import DeactivatedTag from "../../../../common/components/tags/DeactivatedTag";
import Ellipsis from "../../../../common/components/views/Ellipsis";
import { TableSizes } from "../../../../common/constants";
import { CoverageLimit, CoverageLimitFilterPageRequest } from "../../types";

interface Props {
  searchParams: CoverageLimitFilterPageRequest;
  onUpdateClick: (limit: CoverageLimit) => void;
}

export const CoverageLimitTableView = ({ searchParams, onUpdateClick }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isFetching } = useFilterCoverageLimits(searchParams);
  const deleteCoverageLimit = useDeleteCoverageLimit();

  const columns: ColumnsType<CoverageLimit> = [
    {
      key: "institution",
      title: t("common.insuranceInstitution"),
      width: 150,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.institution.name}</Ellipsis>,
    },
    {
      key: "health",
      title: t("coverageLimit.attrs.health"),
      width: 115,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{formatLocaleCurrencyWithMillionLabel(record.health)}</Ellipsis>,
    },
    {
      key: "property",
      title: t("coverageLimit.attrs.property"),
      width: 115,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{formatLocaleCurrencyWithMillionLabel(record.property)}</Ellipsis>,
    },
    {
      key: "coverageLimitEnum",
      title: t("coverageLimit.enums.coverageLimitEnum._label"),
      width: 240,
      ellipsis: { showTitle: false },
      render: (_, record) =>
        record.coverageLimitEnum ? (
          <Ellipsis>{t("coverageLimit.enums.coverageLimitEnum." + record.coverageLimitEnum)}</Ellipsis>
        ) : null,
    },
    {
      key: "deactivated",
      align: "center",
      width: 100,
      render: (_, record) => (record.deactivated ? <DeactivatedTag style={{ marginRight: 0 }} /> : null),
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <>
          <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdateClick(record)} />

          <Divider orientation="vertical" />

          <Popconfirm
            title={t("coverageLimit.helpers.deleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            cancelText={t("common.no")}
            okText={t("common.yes")}
            okType="danger"
            onConfirm={async () => {
              await deleteCoverageLimit.mutateAsync(record.id);
            }}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Card className="card-box">
      <Table<CoverageLimit>
        {...tableStandardProps()}
        loading={isFetching}
        columns={columns}
        dataSource={data?.pageData}
        scroll={{ x: TableSizes.MEDIUM }}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />
    </Card>
  );
};
